import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, Star } from 'lucide-react';
import Logo from './layout/Logo';
import { Footer } from './layout/Footer';
import { SUBSCRIPTION_PLANS } from '../services/subscriptionService';
import { ProblemVsSolutionSection } from './splash/ProblemVsSolutionSection';
import { FeaturesGroupedSection } from './splash/FeaturesGroupedSection';
import { TrustBuilderSection } from './splash/TrustBuilderSection';
import {
  finalCtaContent,
  testimonials,
  testimonialsSectionHeading,
  testimonialsSectionIntro,
} from './splash/landingContent';
import { Seo } from './seo/Seo';

/** Control how the hero image is positioned (e.g. 'center center', '50% 30%', 'center top'). */
const heroBgPosition = '50% 35%';

export const SplashPage: React.FC = () => {
  const rookiePlan = SUBSCRIPTION_PLANS.rookie;
  const proPlan = SUBSCRIPTION_PLANS.pro;
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');

  return (
    <div className="min-h-screen bg-cream">
      {/* Dark backing behind Android's transparent system bars on the splash page */}
      <div
        className="md:hidden pointer-events-none fixed left-0 right-0 top-0 z-[60] bg-[#111827]"
        style={{ height: 'var(--safe-area-inset-top, 0px)' }}
      />
      <div
        className="md:hidden pointer-events-none fixed left-0 right-0 bottom-0 z-[60] bg-[#111827]"
        style={{ height: 'var(--safe-area-inset-bottom, 0px)' }}
      />

      <Seo
        title="Hustli - Youth Sports Practice Management"
        description="AI-powered practice plans, rosters, and game planning for youth football, basketball, soccer, baseball, and more. Less planning, more coaching."
        path="/"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Hustli',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: 'https://hustli.app/',
            description:
              'AI-powered practice plans, rosters, and game planning for youth football, basketball, soccer, baseball, and more. Less planning, more coaching.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Hustli',
            url: 'https://hustli.app/',
            logo: 'https://hustli.app/assets/images/hustli_og_image.jpg',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Hustli',
            url: 'https://hustli.app/',
          },
        ]}
      />
      {/* Header with Logo */}
      <header
        className="fixed top-0 left-0 right-0 bg-[#3A3C3E] z-50"
        style={{ paddingTop: 'var(--safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo className="h-8 text-white" />
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white bg-white/10 hover:bg-white/15 active:bg-white/20 transition-colors min-h-[44px]"
            aria-label="Sign in"
          >
            Sign In
          </Link>
        </div>
      </header>

      {/* Hero Section background: public/assets/images/hero-banner.jpg (40% opacity, position via heroBgPosition) */}
      <section className="relative min-h-[32rem] md:min-h-[36rem] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/images/hero-banner2.jpg)',
            backgroundPosition: heroBgPosition,
          }}
        />
        <div className="absolute inset-0 bg-[#3A3C3E]/70" />
        <div className="relative z-10 px-4 pt-24 pb-20 md:pt-28 md:pb-24 max-w-7xl mx-auto w-full text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 [text-shadow:0_2px_8px_rgba(0,0,0,0.7),0_0_24px_rgba(0,0,0,0.5)]">
            Less Planning. More Coaching.
          </h1>
          <p className="text-xl md:text-2xl text-white mb-4 max-w-3xl mx-auto font-medium [text-shadow:0_2px_6px_rgba(0,0,0,0.8),0_0_16px_rgba(0,0,0,0.6)]">
            AI powered practice plans, roster tools, and game planning for youth sports.
          </p>
          <p className="text-base text-white/90 mb-8 [text-shadow:0_1px_4px_rgba(0,0,0,0.8)]">
            Try Pro free for 7 days — no charge until your trial ends.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="btn-primary text-lg px-8 py-4 bg-primary hover:bg-primary/90 text-white border-0 min-h-[48px] inline-flex items-center justify-center"
            >
              Sign Up Now
            </Link>
          </div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="px-4 py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-dark">
            Choose your plan
          </h2>
          <p className="text-center text-stone text-lg mb-12 max-w-2xl mx-auto">
            Start with Rookie at no cost, or try Pro free for 7 days — unlimited AI planning, multiple teams, and more.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Rookie */}
            <div className="rounded-2xl border-2 border-stone-200 bg-cream/50 p-8 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-6 w-6 text-amber-500" />
                <h3 className="text-xl font-bold text-dark">{rookiePlan.name}</h3>
              </div>
              <div className="mb-6">
                <span className="text-3xl font-bold text-dark">Free</span>
              </div>
              <ul className="space-y-3 flex-1">
                {rookiePlan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="mt-6 w-full text-center btn-primary py-3 rounded-xl font-semibold min-h-[48px] inline-flex items-center justify-center"
              >
                Sign Up Now
              </Link>
            </div>
            {/* Pro */}
            <div className="rounded-2xl border-2 border-primary bg-primary/5 p-8 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-semibold px-4 py-1 rounded-full">
                Most Popular
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Star className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold text-dark">{proPlan.name}</h3>
              </div>

              {/* Billing toggle */}
              <div className="flex items-center bg-white border border-gray-200 rounded-xl p-1 gap-1 mb-5 self-start">
                <button
                  type="button"
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    billingPeriod === 'monthly'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-stone hover:text-dark'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingPeriod('annual')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    billingPeriod === 'annual'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-stone hover:text-dark'
                  }`}
                >
                  Annual
                  {billingPeriod !== 'annual' && (
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                      Save {proPlan.annual.savings}%
                    </span>
                  )}
                </button>
              </div>

              {/* Price display */}
              {billingPeriod === 'annual' ? (
                <div className="mb-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-bold text-primary">${proPlan.annual.monthlyEquivalent.toFixed(2)}</span>
                    <span className="text-stone">/month</span>
                    <span className="ml-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      Save {proPlan.annual.savings}%
                    </span>
                  </div>
                  <p className="text-sm text-stone mt-0.5">
                    Billed ${proPlan.annual.price.toFixed(2)}/year
                    <span className="ml-2 text-xs line-through text-stone/60">${(proPlan.monthly.price * 12).toFixed(2)}/year</span>
                  </p>
                </div>
              ) : (
                <div className="mb-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-primary">${proPlan.monthly.price.toFixed(2)}</span>
                    <span className="text-stone">/month</span>
                  </div>
                  <p className="text-sm text-stone/60 mt-0.5">Switch to annual to save ${((proPlan.monthly.price * 12) - proPlan.annual.price).toFixed(2)}/year</p>
                </div>
              )}

              <div className="mb-5 mt-3 inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full w-fit">
                <span>7-day free trial — no credit card charge until trial ends</span>
              </div>
              <ul className="space-y-3 flex-1">
                {proPlan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="mt-6 w-full text-center py-3 rounded-xl font-semibold bg-primary text-white hover:bg-primary/90 transition-colors min-h-[48px] inline-flex items-center justify-center"
              >
                Start 7-Day Free Trial
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ProblemVsSolutionSection />

      <FeaturesGroupedSection />

      <TrustBuilderSection />

      {/* Social Proof Section */}
      <section className="px-4 py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="w-full flex justify-center mb-4">
            <div className="h-2 w-32 rounded-full bg-gradient-to-r from-primary/70 via-secondary/70 to-primary/30" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 max-w-3xl mx-auto leading-tight bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            {testimonialsSectionHeading}
          </h2>
          <p className="text-center text-stone mb-12 md:mb-14 max-w-xl mx-auto text-sm md:text-base">
            {testimonialsSectionIntro}
          </p>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card">
                <p className="text-stone mb-4 leading-relaxed">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-full mr-4 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-semibold">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-dark">{testimonial.name}</p>
                    <p className="text-sm text-stone">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-12 md:py-16 bg-[#3A3C3E] text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-60"
          aria-hidden
          style={{
            background:
              'radial-gradient(700px 240px at 15% 35%, rgba(46,139,87,0.22), transparent 60%), radial-gradient(600px 220px at 85% 30%, rgba(255,147,79,0.18), transparent 55%)',
          }}
        />
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="relative text-3xl md:text-4xl font-bold mb-4 md:mb-6 leading-tight text-primary">
            {finalCtaContent.heading}
          </h2>
          <p className="relative text-lg md:text-xl mb-8 text-white/85 leading-relaxed">{finalCtaContent.body}</p>
          <Link
            to="/register"
            className="relative inline-flex items-center justify-center bg-white text-dark font-semibold text-lg px-8 py-4 rounded-xl hover:bg-cream transition-colors min-h-[48px] min-w-[200px]"
          >
            {finalCtaContent.buttonLabel}
          </Link>
        </div>
      </section>

      <Footer variant="landing" />
    </div>
  );
};

export default SplashPage;
